import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PriceRangeSlider from '../../common/PriceRangeSlider';

const SECTION_LABEL = { fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' };
const CHECK_LABEL = { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14.5px', cursor: 'pointer' };
const CHECK_INPUT = { width: '17px', height: '17px', accentColor: '#054625' };

const RestaurantFiltersPanel = ({
  filters,
  expandedSections,
  onToggleSection,
  onToggleFilter,
  onClearAllFilters,
  onPriceRangeChange,
  onApplyFilters
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const filterOptions = {
    cuisine: [
      { value: 'african', label: t('filters.restaurant.cuisine.african') },
      { value: 'cameroonian', label: t('filters.restaurant.cuisine.cameroonian') },
      { value: 'ivorian', label: t('filters.restaurant.cuisine.ivorian') },
      { value: 'senegalese', label: t('filters.restaurant.cuisine.senegalese') },
      { value: 'nigerian', label: t('filters.restaurant.cuisine.nigerian') },
      { value: 'ghanaian', label: t('filters.restaurant.cuisine.ghanaian') }
    ],
    rating: [
      { value: '4.5', label: t('filters.restaurant.rating.plus', { value: '4.5' }) },
      { value: '4.0', label: t('filters.restaurant.rating.plus', { value: '4.0' }) },
      { value: '3.5', label: t('filters.restaurant.rating.plus', { value: '3.5' }) }
    ],
    city: [
      { value: 'paris', label: 'Paris' },
      { value: 'lyon', label: 'Lyon' },
      { value: 'marseille', label: 'Marseille' },
      { value: 'toulouse', label: 'Toulouse' },
      { value: 'nice', label: 'Nice' }
    ],
    status: [
      { value: 'open', label: t('filters.restaurant.status.open') },
      { value: 'delivery', label: t('filters.restaurant.status.delivery') },
      { value: 'pickup', label: t('filters.restaurant.status.pickup') }
    ]
  };

  const Section = ({ id, title, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
      <button
        type="button"
        onClick={() => onToggleSection(id)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, ...SECTION_LABEL }}
      >
        <span>{title}</span>
        <span>{expandedSections[id] ? '−' : '+'}</span>
      </button>
      {expandedSections[id] && children}
    </div>
  );

  return (
    <aside style={{ background: '#FCFBF5', borderRadius: '24px', padding: '26px 24px', display: 'flex', flexDirection: 'column', gap: '24px', position: 'sticky', top: '96px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#054625" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 4h18l-7 8v7l-4-2v-5Z"></path></svg>
        <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '19px' }}>{t('filters.restaurant.title')}</strong>
        <button type="button" onClick={onClearAllFilters} style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: 'Archivo, sans-serif', fontSize: '13px', fontWeight: 600, color: '#EF4C23', textDecoration: 'underline' }}>{t('filters.clearAll')}</button>
      </div>

      <Section id="cuisine" title={t('filters.restaurant.sections.cuisine')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filterOptions.cuisine.map((option) => (
            <label key={option.value} style={CHECK_LABEL}>
              <input type="checkbox" checked={filters.cuisine?.includes(option.value) || false} onChange={() => onToggleFilter('cuisine', option.value)} style={CHECK_INPUT} />
              {option.label}
            </label>
          ))}
        </div>
      </Section>

      <Section id="price" title={t('filters.restaurant.sections.price')}>
        <div style={{ marginTop: '2px' }}>
          <PriceRangeSlider
            min={0}
            max={200}
            step={5}
            value={filters.priceRange || { min: 0, max: 200 }}
            onChange={onPriceRangeChange}
            formatLabel={(val) => `€${val}`}
          />
        </div>
      </Section>

      <Section id="rating" title={t('filters.restaurant.sections.rating')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filterOptions.rating.map((option) => (
            <label key={option.value} style={CHECK_LABEL}>
              <input type="checkbox" checked={filters.rating?.includes(option.value) || false} onChange={() => onToggleFilter('rating', option.value)} style={{ ...CHECK_INPUT, accentColor: '#EF4C23' }} />
              {option.label}
            </label>
          ))}
        </div>
      </Section>

      <Section id="city" title={t('filters.restaurant.sections.city')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filterOptions.city.map((option) => (
            <label key={option.value} style={CHECK_LABEL}>
              <input type="checkbox" checked={filters.city?.includes(option.value) || false} onChange={() => onToggleFilter('city', option.value)} style={CHECK_INPUT} />
              {option.label}
            </label>
          ))}
        </div>
      </Section>

      <Section id="status" title={t('filters.restaurant.sections.status')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {filterOptions.status.map((option) => (
            <label key={option.value} style={CHECK_LABEL}>
              <input type="checkbox" checked={filters.status?.includes(option.value) || false} onChange={() => onToggleFilter('status', option.value)} style={CHECK_INPUT} />
              {option.label}
            </label>
          ))}
        </div>
      </Section>

      <button
        type="button"
        onClick={() => onApplyFilters?.()}
        style={{ border: 'none', cursor: 'pointer', background: '#054625', color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '15px', padding: '13px 20px', borderRadius: '999px' }}
      >
        {t('filters.restaurant.apply')}
      </button>

      <div style={{ background: '#FBF9D9', borderRadius: '24px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '17px' }}>{t('filters.restaurant.cookCta.title')}</strong>
        <span style={{ fontSize: '13.5px', lineHeight: 1.5, color: 'rgba(5,16,4,0.74)' }}>{t('filters.restaurant.cookCta.subtitle')}</span>
        <button
          type="button"
          onClick={() => navigate('/business/signup')}
          style={{ alignSelf: 'flex-start', marginTop: '4px', border: 'none', cursor: 'pointer', background: '#054625', color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13.5px', padding: '10px 20px', borderRadius: '999px' }}
        >
          {t('filters.restaurant.cookCta.cta')}
        </button>
      </div>
    </aside>
  );
};

export default RestaurantFiltersPanel;
